import { bySubstance, pageLabel } from '../armKit';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ProfileCard } from '../surface';

type Facts = Map<number, WorkFacts>;

const FLAG = 'substancefirst';
const TOP_COUNT = 3;
const READ_SOURCE = 'profile-liautotest-substance-read';

function chipText(card: ProfileCard, facts: Facts): string | null {
  const parts: string[] = [];

  const pages = pageLabel(facts.get(card.workId)?.pageCount);
  if (pages != null) parts.push(pages);
  if (card.hasDownloadable) parts.push('full text');

  return parts.length === 0 ? null : parts.join(' · ');
}

function chipCards(cards: ProfileCard[], facts: Facts): void {
  cards.forEach((card) => {
    if (card.flag(FLAG) != null) return;
    card.setFlag(FLAG);

    const text = chipText(card, facts);
    if (text != null) card.addChip(text);
  });
}

function promote(card: ProfileCard, ctx: ArmContext): void {
  if (!card.hasHiddenAbstract || card.url == null) return;

  card.expandAbstract();

  const action = card.addAction('Read', card.url);
  action?.addEventListener('click', () => {
    ctx.track(READ_SOURCE, { entityId: card.workId });
  });
}

export const substanceFirst: ArmSpec = {
  onReady: async (ctx) => {
    const facts = await ctx.workFacts();
    if (facts.size === 0) return;

    ctx.reorderCards(bySubstance((workId) => facts.get(workId)?.pageCount));

    // Read back rather than reuse a sorted array: the reorder is scoped per
    // section, so only the DOM knows which three cards ended up on top.
    const ordered = ctx.cards();
    chipCards(ordered, facts);
    ordered.slice(0, TOP_COUNT).forEach((card) => promote(card, ctx));
  },

  // Scroll-appended batches are chipped only: reordering under a reader who is
  // already scrolling costs more than a late batch in upload order.
  onCards: async (cards, ctx) => {
    const facts = await ctx.workFacts();
    if (facts.size === 0) return;

    chipCards(cards, facts);
  },
};
